<template>
  <div class="thread">
    <div ref="scroll" class="thread-messages">
      <div v-if="!chat.state.messages.length" class="thread-empty">
        <p>{{ firstName ? $t('aiAssistant.emptyHintNamed', { name: firstName }) : $t('aiAssistant.emptyHint') }}</p>
        <ul class="examples">
          <li>{{ $t('aiAssistant.example1') }}</li>
          <li>{{ $t('aiAssistant.example2') }}</li>
          <li>{{ $t('aiAssistant.example3') }}</li>
        </ul>
      </div>
      <MessageBubble
        v-for="(msg, i) in chat.state.messages"
        :key="msg.id"
        :message="msg"
        :on-approve="awaitingApprovalOn === i ? chat.approveTool : null"
        :on-approve-and-trust="awaitingApprovalOn === i ? chat.approveToolAndTrust : null"
        :on-reject="awaitingApprovalOn === i ? chat.rejectTool : null"
        :is-already-trusted="pendingToolIsTrusted"
      />
      <div v-if="chat.state.status === 'streaming'" class="streaming-indicator">
        <span></span><span></span><span></span>
      </div>
      <div v-if="chat.state.error" class="thread-error">
        <span class="thread-error-text">{{ chat.state.error }}</span>
        <button type="button" class="thread-retry" @click="handleRetry">
          {{ $t('aiAssistant.retry') }}
        </button>
      </div>
    </div>
    <Composer
      :on-send="handleSend"
      :disabled="composerDisabled"
    />
  </div>
</template>

<script>
import MessageBubble from "./MessageBubble.vue";
import Composer from "./Composer.vue";

export default {
  name: "Thread",
  components: { MessageBubble, Composer },
  props: {
    chat: { type: Object, required: true },
  },
  computed: {
    // Keep the composer usable while idle OR after an error — officers were
    // getting stranded on the error state because they didn't notice the
    // Reset button. Only block during an active stream or a pending approval.
    composerDisabled() {
      const s = this.chat.state.status;
      return s === "streaming" || s === "awaiting-approval";
    },
    awaitingApprovalOn() {
      if (this.chat.state.status !== "awaiting-approval") return -1;
      return this.chat.state.messages.length - 1;
    },
    pendingToolIsTrusted() {
      const pending = this.chat.state.pendingTool;
      return pending ? this.chat.isTrusted(pending.toolName) : false;
    },
    firstName() {
      const fullName = this.$store?.state?.user?.name;
      if (typeof fullName !== "string") return "";
      const trimmed = fullName.trim();
      if (!trimmed) return "";
      return trimmed.split(/\s+/)[0];
    },
  },
  watch: {
    "chat.state.messages": {
      handler() {
        this.$nextTick(() => this.scrollToBottom());
      },
      deep: true,
    },
    "chat.state.status": {
      handler() {
        this.$nextTick(() => this.scrollToBottom());
      },
    },
  },
  methods: {
    scrollToBottom() {
      const el = this.$refs.scroll;
      if (el) el.scrollTop = el.scrollHeight;
    },
    handleSend(text) {
      this.chat.send(text);
    },
    handleRetry() {
      this.chat.retry();
    },
  },
};
</script>

<style scoped>
.thread {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.thread-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}
.thread-empty {
  color: #6b7280;
  font-size: 14px;
  padding: 24px 8px;
  text-align: start;
}
.thread-empty p {
  margin-bottom: 12px;
  font-weight: 500;
}
.examples {
  margin: 0;
  padding-inline-start: 20px;
  font-size: 13px;
  line-height: 1.7;
}
.streaming-indicator {
  display: flex;
  gap: 4px;
  margin-top: 8px;
  padding: 8px 0;
}
.streaming-indicator span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9ca3af;
  animation: blink 1.4s infinite;
}
.streaming-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}
.streaming-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes blink {
  0%, 80%, 100% {
    opacity: 0.3;
  }
  40% {
    opacity: 1;
  }
}
.thread-error {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #b91c1c;
  padding: 10px 12px;
  background: #fee2e2;
  border-radius: 6px;
  font-size: 13px;
  margin-top: 8px;
}
.thread-error-text {
  flex: 1;
  word-break: break-word;
}
.thread-retry {
  flex-shrink: 0;
  background: #b91c1c;
  color: white;
  border: none;
  padding: 5px 12px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.thread-retry:hover {
  background: #991b1b;
}
</style>
